import {
    Form,
    FormControl,
    FormField,
    FormItem
} from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/common/Popover';
import { RADIUS_AROUND_MY_POSITION } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { LocationDetailPage } from '@/pages';
import yup from '@/services/yup';
import { cn } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import debounce from 'lodash.debounce';
import { MapPinIcon } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import LocationItem from './components/LocationItem';
import CurrentMapLocation from './components/map/CurrentMapLocation';
import TitleList from '@/components/general/title/TitleList';
import { SubTitleWithTwoBox } from '@/components/general/title/sub-title/SubTitle';
import { SearchIconCurrent } from '@/components/icon';
import BannerLayout from '@/components/layout/banner-layout';

export default observer(function CurrentLocationPage() {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const sessionToken = uuidv4();

    // store
    const {
        locationEnduserStore: {
            locationDetail,
            locationsCurrent,
            pagingLocationsCurrent,
            keepSearchParams,
            getLocationsCurrent,
            clean,
            setObservable
        },
        bookingStore: {
            setObservable: setObservableBooking
        },
        generalStore: { getPlaceAutoComplete, getLatLngFromPlaceId },
        authStore: { verifyToken }
    } = useStore();

    // validate Schema
    const validateSchema = yup.object().shape({
        address: yup.string(),
        latitude: yup.string().optional().nullable(),
        longitude: yup.string().optional().nullable()
    });

    type FormData = yup.InferType<typeof validateSchema>;
    const form = useForm({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            address: keepSearchParams?.address ?? '',
            latitude: keepSearchParams?.latitude ?? null,
            longitude: keepSearchParams?.longitude ?? null
        }
    });

    const latitudeWatch = form.watch('latitude');
    const longitudeWatch = form.watch('longitude');

    // state
    const [placesSearch, setPlacesSearch] = useState<any[]>([]);
    const [openPopoverSearch, setOpenPopoverSearch] = useState<boolean>(false);

    // lifecycle
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setObservableBooking(
                        'deviceCurrentLocation',
                        {
                            latitude: latitude,
                            longitude: longitude
                        },
                        { isMergeObject: true }
                    );

                    form.setValue('latitude', latitude?.toString());
                    form.setValue('longitude', longitude?.toString());
                },
                (error) => {
                    console.error(error);
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 0
                }
            );
        }
    }, []);

    useEffect(() => {
        if (latitudeWatch && longitudeWatch) {
            onFetch(latitudeWatch, longitudeWatch);
        }

        return () => {
            clean();
        };
    }, [latitudeWatch, longitudeWatch]);

    useEffect(() => {
        // call api check login
        verifyToken();
    }, []);

    useEffect(() => {
        if (placesSearch?.length > 0) {
            setOpenPopoverSearch(true);
        }
    }, [placesSearch]);

    // function
    const onDebounceGetPlaceAutocomplete = useCallback(
        debounce((addr: string) => {
            onGetPlaceAutocomplete(addr);
        }, 500),
        []
    );

    const onGetPlaceAutocomplete = async (addr: string) => {
        let data = [];
        try {
            if (addr.trim()) {
                const response = await flowResult(
                    getPlaceAutoComplete({ input: addr.trim(), sessionToken, languageCode: language })
                );
                data = response;
            }
        } catch (error) {
            console.error(error);
        } finally {
            setPlacesSearch(data);
        }
    };

    const onFetch = async (latitude: any, longitude: any) => {
        await flowResult(getLocationsCurrent(
            {
                radius: RADIUS_AROUND_MY_POSITION,
                latitude: latitude || null,
                longitude: longitude || null
            },
            pagingLocationsCurrent
        ));
        // scrollToTop();
    };

    const onSearch = async (data: FormData) => {
        const { latitude, longitude } = data;
        setObservable('keepSearchParams', data, { isMergeObject: true });
        await onFetch(latitude, longitude);
    };

    return (
        <div className='overflow-x-hidden under-wrapper'>
            <BannerLayout
                title='現在地から拠点を探す'
                subTitle='現在地から拠点を探す'
                url=''
                className='hidden sm:block'
            />
            <div className='wrapper-inner flex flex-col gap-[min(40px,7.7vw)] mt-[min(120px,15vw)]'>
                <SubTitleWithTwoBox
                    title={(
                        <>
                            現在地から
                            <br className='block sm:hidden' />
                            拠点を選択して下さい
                        </>
                    )}
                    sub='SELECT BASE FROM CURRENT LOCATION'
                    titleClassName='break-all'
                />

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSearch)}>
                        <div className='w-full flex justify-center items-center px-[min(40px,5.2vw)] py-[min(40px,7.5vw)] bg-[#F8F7F1] rounded-[20px]'>
                            <FormField
                                control={form.control}
                                name='address'
                                render={({ field }) => (
                                    <FormItem className='w-full !max-w-[540px]'>
                                        <Popover open={openPopoverSearch}>
                                            <PopoverTrigger className='w-full'>
                                                <FormControl>
                                                    <div className='relative'>
                                                        <SearchIconCurrent className='absolute left-[20px] top-1/2 -translate-y-1/2 z-10 size-4 text-primaryWeb cursor-default' />
                                                        <Input
                                                            className='pl-[42px] pr-[20px] py-[10px] bg-[#fff] border border-border-gray rounded-[5px] h-10 leading-lh-nor14 placeholder:text-[#6A6A6A]'
                                                            value={field.value}
                                                            placeholder='検索ワードを入力し下さい'
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                onDebounceGetPlaceAutocomplete(
                                                                    e.target.value
                                                                );
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                className='w-full p-1 !max-w-[min(540px,80vw)]'
                                                align='start'
                                                autoFocus={false}
                                                widthFull={true}
                                                onOpenAutoFocus={e => e.preventDefault()}
                                                onPointerDownOutside={() => setOpenPopoverSearch(false)}
                                                avoidCollisions={false}
                                                side='bottom'
                                                sideOffset={4}
                                            >
                                                {placesSearch?.length > 0 && (
                                                    <div className='divide-y divide-gray-200'>
                                                        {placesSearch.map((place, index) => (
                                                            <div
                                                                key={index}
                                                                className='py-2 px-1 text-sm flex items-center gap-2 hover:cursor-pointer hover:bg-slate-200'
                                                                onClick={async () => {
                                                                    const res = await flowResult(
                                                                        getLatLngFromPlaceId({
                                                                            sessionToken,
                                                                            placeId: place.placeId
                                                                        })
                                                                    );

                                                                    if (res?.location) {
                                                                        const { latitude, longitude } = res?.location;
                                                                        form.setValue('latitude', latitude);
                                                                        form.setValue('longitude', longitude);
                                                                    }

                                                                    if (place?.address) {
                                                                        form.setValue('address', place.address);
                                                                    }
                                                                    setOpenPopoverSearch(false);
                                                                }}
                                                            >
                                                                <div>
                                                                    <MapPinIcon className='w-4 h-4' />
                                                                </div>
                                                                <div>{place?.address}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </PopoverContent>
                                        </Popover>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </form>
                </Form>

                <div className='h-[min(576px,73.5vw)] flex justify-center items-center'>
                    <CurrentMapLocation
                        latitudeSearch={latitudeWatch}
                        longitudeSearch={longitudeWatch}
                    />
                </div>
            </div>

            {(locationDetail && Object.keys(locationDetail).length > 0) && (
                <div>
                    <LocationDetailPage showBanner={false} />
                </div>
            )}

            <div className={cn(
                'wrapper-inner',
                (locationDetail && Object.keys(locationDetail).length > 0) ? 'mt-0' : 'mt-[min(80px,10.25vw)]'
            )}
            >
                <div className='flex flex-col gap-[min(40px,7.7vw)]'>
                    {locationsCurrent?.length > 0 && (
                        <TitleList
                            title={t('sentences.result_current_location', {
                                count: locationsCurrent?.length
                            })}
                        />
                    )}
                    <div className='px-[10px] grid gap-x-[40px] gap-y-[20px] grid-cols-1 point-600:grid-cols-2 point-900:grid-cols-3'>
                        {locationsCurrent?.length > 0 ?
                            (
                                locationsCurrent?.map(item => (
                                    <LocationItem key={item?.id} item={item} />
                                ))
                            ) :
                            (
                                <div className='text-center w-full col-span-4 h-[10vh] sm:h-[16vh] lg:h-[20vh]'>
                                    {t('sentences.no_data')}
                                </div>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
});
